import axios, { AxiosInstance, AxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  Product,
  SKU,
  Entitlement,
  OrgEntitlement,
  ProductOnboardingForm,
  OnboardingResponse,
  Region,
  ProductSKUConfig,
  ProductAddonConfig,
  ProductSKUConfigEntitlement,
  SKUOrgEntitlementConfig,
  ProductAllowedRegion,
  ProvisioningQuestion,
} from '../types';
import { extractAllRegions } from '../utils/utils';
import { oauthService } from './oauthService';
import { getConfig } from '../config/runtimeConfig';

export class ApiService {
  private client: AxiosInstance;

  private static readonly REQUEST_ID_HEADER = 'X-Request-ID';

  // Stack of active operation IDs. Any HTTP call issued while an operation
  // is active inherits the top-of-stack ID, so every server call made in
  // a single user operation shares one X-Request-ID for backend auditing.
  private operationIdStack: string[] = [];

  constructor() {
    // getConfig() is safe here — called after loadConfig() resolves in index.tsx
    this.client = axios.create({
      baseURL: getConfig().apiBaseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add access token to all requests
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const token = oauthService.getOktaAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // Failed to get access token
        }

        const existingRequestId = config.headers?.[ApiService.REQUEST_ID_HEADER];
        if (!existingRequestId) {
          const activeOperationId = this.operationIdStack[this.operationIdStack.length - 1];
          config.headers[ApiService.REQUEST_ID_HEADER] = activeOperationId || this.generateSecureRequestId();
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => Promise.reject(error)
    );
  }

  private generateSecureRequestId(): string {
    return uuidv4();
  }

  // Run a user-initiated operation such that every API call issued from within
  // fn (directly or transitively) shares the same X-Request-ID header, so the
  // backend can correlate all resulting downstream work under a single audit trail.
  public async runOperation<T>(
    fn: (requestId: string) => Promise<T>,
    requestId?: string,
  ): Promise<{ requestId: string; result: T }> {
    const operationId = requestId || this.generateSecureRequestId();
    this.operationIdStack.push(operationId);
    try {
      const result = await fn(operationId);
      return { requestId: operationId, result };
    } finally {
      const idx = this.operationIdStack.lastIndexOf(operationId);
      if (idx !== -1) {
        this.operationIdStack.splice(idx, 1);
      }
    }
  }

  // Header name used for correlating all backend calls issued for one user operation.
  public static readonly REQUEST_ID_HEADER_NAME = 'X-Request-ID';

  // Returns the currently active operation ID (top of stack), or undefined if
  // no operation is active. Callers making raw HTTP requests outside this
  // service should include this value in an X-Request-ID header so the
  // request participates in the same audit trail.
  public getActiveOperationId(): string | undefined {
    return this.operationIdStack[this.operationIdStack.length - 1];
  }

  // Products
  async getProducts(): Promise<Product[]> {
    const response = await this.client.get('config/products');
    return response.data.data;
  }

  async getProductById(id: number): Promise<Product> {
    const response = await this.client.get(`/products/${id}`);
    return response.data;
  }

  async createProduct(product: Partial<Product>): Promise<Product> {
    const response = await this.client.post('/products', product);
    return response.data;
  }

  // SKUs
  async getSKUs(): Promise<SKU[]> {
    const response = await this.client.get('config/skus');
    return response.data.data;
  }

  async getSKUById(id: number): Promise<SKU> {
    const response = await this.client.get(`/skus/${id}`);
    return response.data;
  }

  async createSKU(sku: Partial<SKU>): Promise<SKU> {
    const response = await this.client.post('/skus', sku);
    return response.data;
  }

  // Entitlements
  async getEntitlements(productId?: number): Promise<Entitlement[]> {
    const url = productId ? `/entitlements?product_id=${productId}` : 'config/entitlements';
    const response = await this.client.get(url);
    return response.data.data;
  }

  async createEntitlement(entitlement: Partial<Entitlement>): Promise<Entitlement> {
    const response = await this.client.post('/entitlements', entitlement);
    return response.data;
  }

  // Org Entitlements
  async getOrgEntitlements(productId?: number): Promise<OrgEntitlement[]> {
    const url = productId ? `/org-entitlements?product_id=${productId}` : 'config/org_entitlements';
    const response = await this.client.get(url);
    return response.data.data;
  }

  async createOrgEntitlement(orgEntitlement: Partial<OrgEntitlement>): Promise<OrgEntitlement> {
    const response = await this.client.post('config/org_entitlements', orgEntitlement);
    return response.data;
  }

  // Product SKU Configs
  async getProductSkuConfigs(): Promise<ProductSKUConfig[]> {
    const response = await this.client.get('config/product_sku_configs');
    return response.data.data;
  }

  async createProductSkuConfig(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.client.post('config/product_sku_configs', data);
    return response.data;
  }

  async deleteProductSkuConfig(productId: number, skuId: number): Promise<void> {
    await this.client.delete('config/product_sku_configs', {
      data: {
        ProductID: productId,
        SkuID: skuId,
      },
    });
  }

  // Product SKU Config Entitlements
  async getProductSkuConfigEntitlements(): Promise<ProductSKUConfigEntitlement[]> {
    const response = await this.client.get('config/product_sku_config_entitlements');
    return response.data.data;
  }

  async createProductSkuConfigEntitlement(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.client.post('config/product_sku_config_entitlements', data);
    return response.data;
  }

  // Product Addon Config
  async getProductAddonConfigs(): Promise<ProductAddonConfig[]> {
    const response = await this.client.get('config/product_addon_config');
    return response.data.data;
  }

  async createProductAddonConfig(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.client.post('config/product_addon_config', data);
    return response.data;
  }

  async deleteProductAddonConfig(skuId: number): Promise<void> {
    await this.client.delete('config/product_addon_config', {
      data: {
        SkuID: skuId,
      },
    });
  }

  // Regions
  async getRegions(): Promise<Region[]> {
    const response = await this.client.get('config/regional_hierarchy');
    return extractAllRegions(response.data);
  }

  async getProductAllowedRegions(productId: number): Promise<ProductAllowedRegion[]> {
    const response = await this.client.get('config/product_allowed_regions');
    const all: ProductAllowedRegion[] = response.data.data || [];
    return all.filter((r) => r.product_id === productId);
  }

  async getAllProductAllowedRegions(): Promise<ProductAllowedRegion[]> {
    const response = await this.client.get('config/product_allowed_regions');
    return response.data.data || [];
  }

  async getProvisioningQuestions(productId: number): Promise<ProvisioningQuestion[]> {
    const response = await this.client.get('config/provisioning_questions');
    const all: ProvisioningQuestion[] = response.data.data || [];
    return all.filter((q) => q.product_id === productId);
  }

  async getAllProvisioningQuestions(): Promise<ProvisioningQuestion[]> {
    const response = await this.client.get('config/provisioning_questions');
    return response.data.data || [];
  }

  // Main onboarding endpoint
  async onboardProduct(data: ProductOnboardingForm): Promise<OnboardingResponse> {
    const response = await this.client.post('/onboarding/product', data);
    return response.data;
  }

  // Separate onboarding for SKU only
  async onboardSKU(data: Partial<ProductOnboardingForm>): Promise<OnboardingResponse> {
    const response = await this.client.post('/onboarding/sku', data);
    return response.data;
  }

  // Onboarding for addon SKU
  async onboardAddonSKU(data: Partial<ProductOnboardingForm>): Promise<OnboardingResponse> {
    const response = await this.client.post('/onboarding/addon-sku', data);
    return response.data;
  }

  // Bulk insert for product onboarding
  async bulkInsert(payload: Record<string, unknown>): Promise<OnboardingResponse> {
    const response = await this.client.post('/config/bulk_insert', payload);
    return response.data;
  }

  // Bulk update for modifying existing records
  async bulkUpdate(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await this.client.patch('/config/bulk_update', payload);
    return response.data;
  }

  // Delete entitlement by ID
  async deleteEntitlement(id: number): Promise<void> {
    await this.client.delete('config/entitlements', {
      data: { ID: id },
    });
  }

  // Delete org entitlement by ID
  async deleteOrgEntitlement(id: number): Promise<void> {
    await this.client.delete('config/org_entitlements', {
      data: { ID: id },
    });
  }

  // Delete provisioning question by ID
  async deleteProvisioningQuestion(id: number): Promise<void> {
    await this.client.delete('config/provisioning_questions', {
      data: { ID: id },
    });
  }

  // Delete a product allowed region
  async deleteProductAllowedRegion(productId: number, regionName: string): Promise<void> {
    await this.client.delete('config/product_allowed_regions', {
      data: { ProductID: productId, RegionName: regionName },
    });
  }

  // Delete a single product_sku_config_entitlement row
  async deleteProductSkuConfigEntitlement(
    productId: number,
    skuId: number,
    entitlementId: number,
  ): Promise<void> {
    await this.client.delete('config/product_sku_config_entitlements', {
      data: {
        ProductSkuConfigProductID: productId,
        ProductSkuConfigSkuID: skuId,
        EntitlementID: entitlementId,
      },
    });
  }

  // Create SKU org entitlement configs for existing SKU
  async createSKUOrgEntitlementConfigs(payload: { sku_id: number; configs: Array<{ sku_id: number; org_entitlement_id: number }> }): Promise<OnboardingResponse> {
    const response = await this.client.post('/config/sku_org_entitlement_configs', payload);
    return response.data;
  }

  // Get SKU org entitlement configs
  async getSKUOrgEntitlementConfigs(): Promise<SKUOrgEntitlementConfig[]> {
    const response = await this.client.get('/config/sku_org_entitlement_configs');
    return response.data.data || [];
  }

  // Delete SKU org entitlement config
  async deleteSKUOrgEntitlementConfig(skuId: number, orgEntitlementId: number): Promise<void> {
    await this.client.delete('/config/sku_org_entitlement_configs', {
      data: {
        SkuID: skuId,
        OrgEntitlementID: orgEntitlementId,
      },
    });
  }
}

// Lazy singleton — ApiService is instantiated on first use, NOT at module import time.
// This ensures getConfig() is only called after loadConfig() has resolved in index.tsx.
let _apiService: ApiService | null = null;
export const apiService = new Proxy({} as ApiService, {
  get(_target, prop) {
    if (!_apiService) {
      _apiService = new ApiService();
    }
    return (_apiService as ApiService)[prop as keyof ApiService];
  },
});
export default apiService;
